/********************************************************************
 * @author:      Kaven
 * @email:       kaven@wuwenkai.com
 * @website:     http://blog.kaven.xyz
 * @file:        [Kaven-Basic] /src/libs/Hash.ts
 * @create:      2021-12-17 10:53:01.473
 * @modify:      2025-07-11 20:47:10.534
 * @version:     6.0.0
 * @times:       27
 * @lines:       349
 * @copyright:   Copyright © 2021-2025 Kaven. All Rights Reserved.
 * @description: [description]
 * @license:
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 ********************************************************************/

import { TSyncHashInput, TAsyncHashInput } from "./type/advanced";
import { KavenCRC } from "./hash/KavenCRC";
import { KavenMD5 } from "./hash/KavenMD5";
import { KavenSHA } from "./hash/KavenSHA";
import { KavenSHA3 } from "./hash/KavenSHA3";

export const MD5 = new KavenMD5();
export const CRC32 = new KavenCRC(KavenCRC.CRC32);
export const CRC64 = new KavenCRC(KavenCRC.CRC64_ECMA_XZ);
export const SHA = new KavenSHA();
export const SHA3 = new KavenSHA3();

/**
 * @since 1.1.13
 * @version 2025-05-20
 */
export function GetMD5(input: TSyncHashInput) {
    return MD5.Compute(input);
}

/**
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetMD5Async(input: TAsyncHashInput) {
    return await MD5.ComputeAsync(input);
}

/**
 * @since 1.1.20
 * @version 2025-05-20
 */
export function GetCRC32(input: TSyncHashInput) {
    return CRC32.Compute(input);
}

/**
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetCRC32Async(input: TAsyncHashInput) {
    return await CRC32.ComputeAsync(input);
}

/**
 * @since 1.1.20
 * @version 2025-05-20
 */
export function GetCRC64(input: TSyncHashInput) {
    return CRC64.Compute(input);
}

/**
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetCRC64Async(input: TAsyncHashInput) {
    return await CRC64.ComputeAsync(input);
}

/**
 * Warning: This algorithm is now considered vulnerable and should not be used.
 * @since 1.1.20
 * @version 2025-05-20
 */
export function GetSHA1(input: TSyncHashInput) {
    return SHA.ComputeSHA1(input);
}

/**
 * Warning: This algorithm is now considered vulnerable and should not be used.
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA1Async(input: TAsyncHashInput) {
    return await SHA.ComputeSHA1Async(input);
}

// #region SHA-2

/**
 * SHA-224
 * @since 1.1.20
 * @version 2025-05-20
 */
export function GetSHA224(input: TSyncHashInput) {
    return SHA.ComputeSHA224(input);
}

/**
 * SHA-224
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA224Async(input: TAsyncHashInput) {
    return await SHA.ComputeSHA224Async(input);
}

/**
 * SHA-256
 * @since 1.1.20
 * @version 2025-05-20
 */
export function GetSHA256(input: TSyncHashInput) {
    return SHA.ComputeSHA256(input);
}

/**
 * SHA-256
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA256Async(input: TAsyncHashInput) {
    return await SHA.ComputeSHA256Async(input);
}

/**
 * SHA-384
 * @since 1.1.20
 * @version 2025-05-20
 */
export function GetSHA384(input: TSyncHashInput) {
    return SHA.ComputeSHA384(input);
}

/**
 * SHA-384
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA384Async(input: TAsyncHashInput) {
    return await SHA.ComputeSHA384Async(input);
}

/**
 * SHA-512
 * @since 1.1.20
 * @version 2025-05-20
 */
export function GetSHA512(input: TSyncHashInput) {
    return SHA.ComputeSHA512(input);
}

/**
 * SHA-512
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA512Async(input: TAsyncHashInput) {
    return await SHA.ComputeSHA512Async(input);
}

/**
 * @since 1.1.20
 * @version 2025-05-20
 */
export function GetSHA512T(input: TSyncHashInput, t: number) {
    return SHA.ComputeSHA512T(input, t);
}

/**
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA512TAsync(input: TAsyncHashInput, t: number) {
    return await SHA.ComputeSHA512TAsync(input, t);
}

/**
 * SHA-512/224
 * @since 4.0.0
 * @version 2025-05-20
 */
export function GetSHA512T224(input: TSyncHashInput) {
    return SHA.ComputeSHA512T(input, 224);
}

/**
 * SHA-512/224
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA512T224Async(input: TAsyncHashInput) {
    return await SHA.ComputeSHA512TAsync(input, 224);
}

/**
 * SHA-512/256
 * @since 4.0.0
 * @version 2025-05-20
 */
export function GetSHA512T256(input: TSyncHashInput) {
    return SHA.ComputeSHA512T(input, 256);
}

/**
 * SHA-512/256
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA512T256Async(input: TAsyncHashInput) {
    return await SHA.ComputeSHA512TAsync(input, 256);
}
// #endregion

// #region SHA-3

/**
 * SHA3-224
 * @since 4.0.0
 * @version 2025-05-20
 */
export function GetSHA3P224(input: TSyncHashInput) {
    return SHA3.Compute224(input);
}

/**
 * SHA3-224
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA3P224Async(input: TAsyncHashInput) {
    return await SHA3.Compute224Async(input);
}

/**
 * SHA3-256
 * @since 4.0.0
 * @version 2025-05-20
 */
export function GetSHA3P256(input: TSyncHashInput) {
    return SHA3.Compute256(input);
}

/**
 * SHA3-256
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA3P256Async(input: TAsyncHashInput) {
    return await SHA3.Compute256Async(input);
}

/**
 * SHA3-384
 * @since 4.0.0
 * @version 2025-05-20
 */
export function GetSHA3P384(input: TSyncHashInput) {
    return SHA3.Compute384(input);
}

/**
 * SHA3-384
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA3P384Async(input: TAsyncHashInput) {
    return await SHA3.Compute384Async(input);
}

/**
 * SHA3-512
 * @since 4.0.0
 * @version 2025-05-20
 */
export function GetSHA3P512(input: TSyncHashInput) {
    return SHA3.Compute512(input);
}

/**
 * SHA3-512
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHA3P512Async(input: TAsyncHashInput) {
    return await SHA3.Compute512Async(input);
}

/**
 * SHAKE128
 * @since 4.0.0
 * @version 2025-05-20
 */
export function GetSHAKE128(input: TSyncHashInput, outputBits: number) {
    return SHA3.ComputeSHAKE128(input, outputBits);
}

/**
 * SHAKE128
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHAKE128Async(input: TAsyncHashInput, outputBits: number) {
    return await SHA3.ComputeSHAKE128Async(input, outputBits);
}

/**
 * SHAKE256
 * @since 4.0.0
 * @version 2025-05-20
 */
export function GetSHAKE256(input: TSyncHashInput, outputBits: number) {
    return SHA3.ComputeSHAKE256(input, outputBits);
}

/**
 * SHAKE256
 * @since 4.5.0
 * @version 2025-05-20
 */
export async function GetSHAKE256Async(input: TAsyncHashInput, outputBits: number) {
    return await SHA3.ComputeSHAKE256Async(input, outputBits);
}

// #endregion
